<template>
  <div class="exports-page">
    <!-- Page Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">
            {{ t('exports.title') }}
          </h1>
        </div>
      </div>
      <p class="page-description">
        {{ t('exports.summary') }}
      </p>
    </div>

    <!-- Content Section with DataTable -->
    <SpmTableAPI
      v-if="apiCall && columns.length > 0"
      ref="childComponentRef"
      :api-call="apiCall"
      :columns="columns"
      :show-reload-button="true"
      data-key="id"
      :initial-sort="{ field: 'created_at', order: 'desc' }"
      :row-actions-options="rowActions"
    >
      <!-- Type column with dates -->
      <template #cell-type="{ data }">
        <div class="type-cell">
          <div class="type-label">
            {{ getExportTypeLabel(data.export_type) }}
            <SpmBadge
              :label="`#${data.export_job_id}`"
              :colors="{ bg: '#e0f2fe', color: '#0369a1' }"
              size="sm"
              variant="soft"
            />
          </div>
          <div class="type-dates">
            <div class="date-row">
              <SpmDateDisplay
                :value="data.created_at"
                format="datetime"
                icon="fa-light fa-calendar-circle-plus"
                size="sm"
              />
            </div>
          </div>
        </div>
      </template>

      <!-- Status column with badges -->
      <template #cell-status="{ data }">
        <SpmBadge
          :label="getStatusLabel(data.status)"
          :icon="getStatusIcon(data.status)"
          :colors="getStatusColors(data.status)"
        />
      </template>
    </SpmTableAPI>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import type { SpmColumnConfig, ApiCallConfig, RowActionOption } from '@/components/table-rest-api/types';
import { NestApiType } from '@/composables/nestApi';
import { defineComponent, ref } from 'vue';

export default defineComponent({
  name: 'ExportsList',

  components: {
    SpmTableAPI,
    SpmBadge,
    SpmDateDisplay,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();

    const childComponentRef = ref();

    // API configuration
    const apiCall = ref<ApiCallConfig>({
      type_api: NestApiType.CLIENT_API,
      path: '/export',
      secure: true,
    });

    // Columns configuration
    const columns: SpmColumnConfig[] = [
      {
        id: 'export_job_id',
        header: '',
        fields: ['export_job_id'],
        hidden: true,
      },
      {
        id: 'type',
        header: t('exports.headers.type'),
        fields: [{
          name: 'export_type',
          label: t('exports.headers.type'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('exports.types.contacts'), value: 'contacts' },
              { label: t('exports.types.campaign_automation'), value: 'campaign_automation' },
              { label: t('exports.types.campaign_bulk'), value: 'campaign_bulk' },
              { label: t('exports.types.template'), value: 'template' },
              { label: t('exports.types.consent'), value: 'consent' },
              { label: t('exports.types.reject'), value: 'reject' },
              { label: t('exports.types.stats'), value: 'stats' },
              { label: t('exports.types.stats_report'), value: 'stats_report' },
            ],
          },
        }, 'created_at', 'updated_at'],
      },
      {
        id: 'status',
        header: t('exports.headers.status'),
        fields: [{
          name: 'status',
          label: t('exports.headers.status'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('exports.statuses.pending'), value: 'pending' },
              { label: t('exports.statuses.in_progress'), value: 'in_progress' },
              { label: t('exports.statuses.completed'), value: 'completed' },
              { label: t('exports.statuses.failed'), value: 'failed' },
            ],
          },
        }],
      },
    ];

    /**
     * Get export type label
     */
    const getExportTypeLabel = (type: string): string => {
      const typeMap: Record<string, string> = {
        contacts: t('exports.types.contacts'),
        campaign_automation: t('exports.types.campaign_automation'),
        campaign_bulk: t('exports.types.campaign_bulk'),
        template: t('exports.types.template'),
        consent: t('exports.types.consent'),
        reject: t('exports.types.reject'),
        stats: t('exports.types.stats'),
        stats_report: t('exports.types.stats_report'),
      };
      return typeMap[type] || type;
    };

    /**
     * Get status label
     */
    const getStatusLabel = (status: string): string => {
      const statusMap: Record<string, string> = {
        pending: t('exports.statuses.pending'),
        in_progress: t('exports.statuses.in_progress'),
        completed: t('exports.statuses.completed'),
        failed: t('exports.statuses.failed'),
      };
      return statusMap[status] || status;
    };

    /**
     * Get status icon
     */
    const getStatusIcon = (status: string): string => {
      const iconMap: Record<string, string> = {
        pending: 'fa-solid fa-clock',
        in_progress: 'fa-solid fa-spinner fa-spin',
        completed: 'fa-solid fa-check-circle',
        failed: 'fa-solid fa-times-circle',
      };
      return iconMap[status] || 'fa-solid fa-question';
    };

    /**
     * Get status colors for SpmBadge
     */
    const getStatusColors = (status: string): { bg: string; color: string } => {
      const colorMap: Record<string, { bg: string; color: string }> = {
        pending: { bg: 'var(--blue-100)', color: 'var(--blue-700)' },
        in_progress: { bg: 'var(--yellow-100)', color: 'var(--yellow-700)' },
        completed: { bg: 'var(--green-100)', color: 'var(--green-700)' },
        failed: { bg: 'var(--red-100)', color: 'var(--red-700)' },
      };
      return colorMap[status] || { bg: 'var(--gray-100)', color: 'var(--gray-700)' };
    };

    interface ExportRow {
      export_job_id: number;
      export_type: string;
      status: string;
      created_at: string;
      updated_at?: string;
    }

    /**
     * Handle download action
     */
    const handleDownload = (rowData: ExportRow) => {
      router.push({
        name: 'storage.download',
        params: {
          downloadType: 'export',
          idExport: rowData.export_job_id.toString(),
        },
      });
    };

    // Row actions configuration
    const rowActions: RowActionOption[] = [
      {
        label: t('exports.actions.download'),
        icon: 'fa-solid fa-download',
        command: (rowData: ExportRow) => handleDownload(rowData),
        visible: (rowData: ExportRow) => rowData.status === 'completed',
      },
    ];

    return {
      t,
      childComponentRef,
      apiCall,
      columns,
      rowActions,
      getExportTypeLabel,
      getStatusLabel,
      getStatusIcon,
      getStatusColors,
    };
  },
});
</script>

<style scoped lang="scss">
.page-header {
  margin-bottom: 1.5rem;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .page-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }

  .page-description {
    color: #64748b;
    font-size: 0.875rem;
    margin: 0;
  }
}

.type-cell {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .type-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #1e293b;
  }

  .type-dates {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.75rem;
    color: #64748b;

    .date-row {
      display: flex;
      gap: 0.5rem;
      align-items: center;

      .date-label {
        font-weight: 500;
      }
    }
  }
}
</style>
